import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type ApvDashboardStatus =
  | 'all'
  | 'my_pending'
  | 'my_approved'
  | 'my_rejected'
  | 'committee_processed'
  | 'committee_pending'
  | 'committee_rejected'
  | 'my_not_involved'
  | 'group_approved'
  | 'group_rejected';

export interface ApvDashboardSummaryRow {
  totalApplication?: number | string;
  myPending?: number | string;
  myApproved?: number | string;
  myRejected?: number | string;
  committeeProcessed?: number | string;
  committeePending?: number | string;
  committeeRejected?: number | string;
  myNotInvolved?: number | string;
}

export interface ApvDashboardItem {
  id?: string;
  requestId?: string;
  applicationNo: string;
  registrationTypeName?: string;
  firmName: string;
  mobileNo: string;
  inspectionStatus?: string;
  committeeStatus?: string;
  committeeMembersStatus?: string;
  ceStatus?: string;

  email?: string;
  powerStationName?: string;
  levelName?: string;
  levelNo?: string;
  status?: string;
  approverStatus?: string;
}

export interface ApvDashboardPagination {
  page: number;
  perPage: number;
  totalRecords: number;
  totalPages: number;
}

export interface ApvDashboardData {
  summary?: ApvDashboardSummaryRow[] | ApvDashboardSummaryRow;
  list: ApvDashboardItem[];
  pagination?: ApvDashboardPagination;
}

export interface ApvDashboardResponse {
  status: boolean;
  message: string;
  data?: ApvDashboardData;
  summary?: ApvDashboardSummaryRow[] | ApvDashboardSummaryRow;
  list?: ApvDashboardItem[];
  pagination?: ApvDashboardPagination;
}

export interface PendingApprovalsResponse {
  status: boolean;
  message: string;
  data: any; // Typically contains the list of items and pagination metadata
}

export interface CeAshItem {
  ashRequestId: string;
  totalUsers: number;
  perQuantity: number;
  totalAllocated: number;
  totalLifted: number;
  totalBalance: number;
  financialYear: string;
  quarterName: string;
  ashCategoryName: string;
  powerStationName: string;
  createdAt: string;
}

export interface CeAshDashboardResponse {
  status: boolean;
  message: string;
  data?: {
    list: CeAshItem[];
    pagination?: ApvDashboardPagination;
  };
}

export interface CeAshDetailsData {
  ash: {
    ashRequestId: string;
    totalUsers: number;
    perQuantity: number;
    totalAllocated: number;
    totalLifted: number;
    totalBalance: number;
    financialYear: string;
    quarterName: string;
    ashCategoryName: string;
    powerStationName: string;
    createdAt: string;
  };
  users: Array<{
    id: string;
    userId: string;
    fullName: string;
    mobileNo: string;
    email: string;
    allocatedQuantity: number;
    liftedQuantity: number;
    balanceQuantity: number;
  }>;
}

export interface CeAshDetailsResponse {
  status: boolean;
  message: string;
  data?: CeAshDetailsData;
}

@Injectable({
  providedIn: 'root',
})
export class ApprovalApiService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) { }

  /**
   * Chief Engineer (APV) Dashboard
   * API: GET /users/apv/dashboard
   */
  getApvDashboard(
    status: ApvDashboardStatus = 'all',
    page = 1,
    perPage = 10,
    committeeId?: number,
  ): Observable<ApvDashboardResponse> {
    let params = new HttpParams()
      .set('status', status)
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    if (committeeId !== undefined) {
      params = params.set('id', committeeId.toString());
    }

    return this.http.get<ApvDashboardResponse>(`${this.baseUrl}/users/apv/dashboard`, { params });
  }

  /**
   * Inspection Officer Dashboard
   * API: GET /users/insp/dashboard
   */
  getInspDashboard(
    status: ApvDashboardStatus = 'all',
    page = 1,
    perPage = 10,
    search?: string,
  ): Observable<ApvDashboardResponse> {
    let params = new HttpParams()
      .set('status', status)
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    if (search) {
      params = params.set('search', search);
    }

    return this.http.get<ApvDashboardResponse>(`${this.baseUrl}/users/insp/dashboard`, { params });
  }

  /**
   * Inspection Status Update
   * API: POST /users/insp/status-update
   * Updates inspection status (APPROVED / REJECTED) with remarks and document (documentTypeId: 18).
   */
  inspectionStatusUpdate(payload: {
    inspectionTrackingId: number | string;
    status: 'APPROVED' | 'REJECTED';
    remarks: string;
    documents: { documentTypeId: number; documentUploadId: number | string }[];
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/users/insp/status-update`, payload);
  }

  /**
   * Fetch the pending approvals list for users (Vendor Registrations).
   * @param page Current page number (default: 1)
   * @param perPage Optional items per page
   */
  getPendingList(page: number = 1, perPage?: number): Observable<PendingApprovalsResponse> {
    let params = new HttpParams().set('page', page.toString());
    if (perPage) {
      params = params.set('perPage', perPage.toString());
    }
    return this.http.get<PendingApprovalsResponse>(`${this.baseUrl}users/approvals/pending-list`, {
      params,
    });
  }

  /**
   * Fetch registration preview details for a specific requestId.
   * API: GET /application/preview/{requestId}
   */
  getApplicationPreview(requestId: string | number): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}application/preview/${requestId}`);
  }

  /**
   * Download uploaded document.
   * API: GET /application/doc-download/{documentUploadId}
   */
  downloadDocument(documentUploadId: string | number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}application/doc-download/${documentUploadId}`, {
      responseType: 'blob',
    });
  }

  /**
   * Approve a vendor registration as Chief Engineer (APV) or Inspection.
   * API: POST /users/apv/approvals/approve/{requestId}
   */
  approveApvRegistration(
    requestId: string | number,
    remarks: string,
    documentUploadId?: number | string,
  ): Observable<any> {
    const payload: any = { remarks };
    if (documentUploadId) {
      payload.documentUploadId = documentUploadId;
    }
    return this.http.post<any>(`${this.baseUrl}users/apv/approvals/approve/${requestId}`, payload);
  }

  /**
   * Reject a vendor registration as Chief Engineer (APV) or Inspection.
   * API: POST /users/apv/approvals/reject/{requestId}
   */
  rejectApvRegistration(
    requestId: string | number,
    remarks: string,
    documentUploadId?: number | string,
  ): Observable<any> {
    const payload: any = { remarks };
    if (documentUploadId) {
      payload.documentUploadId = documentUploadId;
    }
    return this.http.post<any>(`${this.baseUrl}users/apv/approvals/reject/${requestId}`, payload);
  }

  /**
   * Approve a vendor registration application.
   * API: POST /users/approvals/approve/{requestId}
   */
  approveRegistration(requestId: string | number, remarks: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}users/approvals/approve/${requestId}`, { remarks });
  }

  /**
   * Reject a vendor registration application.
   * API: POST /users/approvals/reject/{requestId}
   */
  rejectRegistration(requestId: string | number, remarks: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}users/approvals/reject/${requestId}`, { remarks });
  }

  /**
   * Fetch Chief Engineer Ash Dashboard data
   */
  getCeAshDashboard(
    financeYearId: number,
    quarterId: number,
    ashCategoryId: number,
    page: number = 1,
    perPage: number = 10,
  ): Observable<CeAshDashboardResponse> {
    const params = new HttpParams()
      .set('financeYearId', financeYearId.toString())
      .set('quarterId', quarterId.toString())
      .set('ashCategoryId', ashCategoryId.toString())
      .set('page', page.toString())
      .set('perPage', perPage.toString());

    return this.http.get<any>(`${this.baseUrl}users/apv/ash`, { params }).pipe(
      map((res) => {
        if (res?.data?.list) {
          res.data.list = res.data.list.map((item: any) => ({
            ...item,
            totalUsers: Number(item.totalUsers) || 0,
            perQuantity: Number(item.perQuantity) || 0,
            totalAllocated: Number(item.totalAllocated) || 0,
            totalLifted: Number(item.totalLifted) || 0,
            totalBalance: Number(item.totalBalance || item.totalbalance) || 0,
          }));
        }
        return res as CeAshDashboardResponse;
      }),
    );
  }

  /**
   * Fetch Chief Engineer Ash Details
   */
  getCeAshDetails(ashRequestId: string): Observable<CeAshDetailsResponse> {
    return this.http.get<any>(`${this.baseUrl}users/apv/ash/${ashRequestId}`).pipe(
      map((res) => {
        if (res?.data) {
          if (res.data.ash) {
            res.data.ash = {
              ...res.data.ash,
              totalUsers: Number(res.data.ash.totalUsers) || 0,
              perQuantity: Number(res.data.ash.perQuantity) || 0,
              totalAllocated: Number(res.data.ash.totalAllocated) || 0,
              totalLifted: Number(res.data.ash.totalLifted) || 0,
              totalBalance: Number(res.data.ash.totalBalance || res.data.ash.totalbalance) || 0,
            };
          }
          if (Array.isArray(res.data.users)) {
            res.data.users = res.data.users.map((u: any) => ({
              ...u,
              allocatedQuantity: Number(u.allocatedQuantity) || 0,
              liftedQuantity: Number(u.liftedQuantity) || 0,
              balanceQuantity: Number(u.balanceQuantity || u.balancequantity) || 0,
            }));
          }
        }
        return res as CeAshDetailsResponse;
      }),
    );
  }

  /**
   * API: GET /users/apv/inspection
   * Fetches the inspection list and dashboard card counts.
   */
  getInspectionList(status?: string, page: number = 1, perPage: number = 10): Observable<any> {
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    params = params.set('page', String(page));
    params = params.set('perPage', String(perPage));
    return this.http.get<any>(`${this.baseUrl}users/apv/inspection`, { params });
  }

  /**
   * API: GET /admin/masters/inspection-groups/user-dropdown
   * Fetches the individual users available for inspection allocation.
   */
  getInspectionUserDropdown(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}admin/masters/inspection-groups/user-dropdown`);
  }

  /**
   * API: GET /users/apv/inspection/group-dropdown
   * Fetches the inspection groups available for group allocation.
   */
  getInspectionGroupDropdown(): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}users/apv/inspection/group-dropdown`);
  }

  /**
   * API: POST /users/apv/inspection
   * Submits inspection allocation (supports both User and Group assignment types).
   * assignmentType: 1 = User, 2 = Group
   */
  submitInspectionAllocation(payload: {
    assignmentType: 1 | 2;
    allocateUserId?: number;
    allocateGroupId?: number;
    allocateRequests: number[];
  }): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}users/apv/inspection`, payload);
  }
}
